import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Tabs,
  Tab,
} from '@mui/material';
import { ListaAcessoOutrosComponentes } from '../acesso-outros-componentes/ListaAcessoOutrosComponentes';
import { ListaAcessoOutrosSistemas } from '../acesso-outros-sistemas/ListaAcessoOutrosSistemas';
import { C4ModelSistemasService } from '../../../../../../shared/services/api/c4-model/SistemasService';
import { ApiException } from '../../../../../../shared/services/api/ApiException';

type AcessoOutroComponente = {
  id: string;
  componente: string;
};

type AcessoOutroSistema = {
  id: string;
  sistema: string;
  sistemaId?: string;
};

type Sistema = {
  id: string;
  nome: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  onSave?: (values: any) => void;
  initialValues?: {
    id?: string;
    nome?: string;
    acessoOutrosComponentes?: AcessoOutroComponente[];
    acessoOutrosSistemas?: AcessoOutroSistema[];
  };
};

export const ModalAcessoComponentes: React.FC<Props> = ({
  open,
  onClose,
  onSave,
  initialValues,
}) => {
  const [nome, setNome] = useState('');
  const [acessoOutrosComponentes, setAcessoOutrosComponentes] = useState<
    AcessoOutroComponente[]
  >([]);
  const [acessoOutrosSistemas, setAcessoOutrosSistemas] = useState<
    AcessoOutroSistema[]
  >([]);
  const [availableSistemas, setAvailableSistemas] = useState<Sistema[]>([]);
  const [isValid, setIsValid] = useState(false);
  const [isLoadingSistemas, setIsLoadingSistemas] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    if (open) {
      // Carregar sistemas disponíveis
      loadAvailableSistemas();

      if (initialValues) {
        setNome(initialValues.nome || '');
        setAcessoOutrosComponentes(initialValues.acessoOutrosComponentes || []);
        setAcessoOutrosSistemas(initialValues.acessoOutrosSistemas || []);
      } else {
        setNome('');
        setAcessoOutrosComponentes([]);
        setAcessoOutrosSistemas([]);
      }
    }
  }, [open, initialValues]);

  const loadAvailableSistemas = async () => {
    setIsLoadingSistemas(true);
    try {
      const result = await C4ModelSistemasService.getAll('1', 100, 0);
      if (result instanceof ApiException) {
        setAvailableSistemas([]);
      } else {
        // Mapear os sistemas para o formato esperado
        const sistemas = result.data.map((sistema) => ({
          id: sistema.id,
          nome: sistema.nome,
        }));
        setAvailableSistemas(sistemas);
      }
    } catch (error) {
      setAvailableSistemas([]);
    } finally {
      setIsLoadingSistemas(false);
    }
  };

  useEffect(() => {
    const allFilled = Boolean(nome.trim());
    setIsValid(allFilled);
  }, [nome]);

  useEffect(() => {
    if (!open) {
      // Limpar campos apenas quando o modal for fechado
      setNome('');
      setAcessoOutrosComponentes([]);
      setAcessoOutrosSistemas([]);
      setActiveTab(0); // Resetar para a primeira tab
    }
  }, [open]);

  const handleSave = () => {
    const values: any = {
      nome,
      acessoOutrosComponentes,
      acessoOutrosSistemas,
    };
    if (initialValues && initialValues.id) values.id = initialValues.id;
    if (onSave) onSave(values);
    onClose();
  };

  const handleAcessoOutrosComponentesChange = (
    novoAcessoOutrosComponentes: AcessoOutroComponente[]
  ) => {
    setAcessoOutrosComponentes(novoAcessoOutrosComponentes);
  };

  const handleAcessoOutrosSistemasChange = (
    novoAcessoOutrosSistemas: AcessoOutroSistema[]
  ) => {
    setAcessoOutrosSistemas(novoAcessoOutrosSistemas);
  };

  const handleClose = () => {
    onClose();
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  return (
    <Dialog open={!!open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>
        {initialValues?.id ? 'Editar Componente' : 'Novo Componente'}
      </DialogTitle>
      <DialogContent dividers>
        <Box display="flex" flexDirection="column" gap={3} sx={{ pt: 1 }}>
          {/* Dados básicos do componente */}
          <Card variant="outlined">
            <CardHeader
              title="Informações do Componente"
              titleTypographyProps={{ variant: 'h6' }}
            />
            <CardContent>
              <TextField
                fullWidth
                label="Nome"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                placeholder="Nome do componente"
                autoFocus
              />
            </CardContent>
          </Card>

          {/* Seção de Acessos */}
          <Typography variant="h5" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
            Configuração de Acessos
          </Typography>

          <Card variant="outlined">
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              aria-label="abas de acesso"
              variant="fullWidth"
              sx={{
                borderBottom: 1,
                borderColor: 'divider',
                '& .MuiTab-root': {
                  minWidth: 0,
                  px: 2,
                },
              }}
            >
              <Tab
                iconPosition="start"
                label={`Acesso a Outros Componentes ${
                  acessoOutrosComponentes.length > 0
                    ? `(${acessoOutrosComponentes.length})`
                    : ''
                }`}
                id="tab-0"
                aria-controls="tabpanel-0"
                sx={{
                  textTransform: 'none',
                  fontWeight: 'medium',
                  minHeight: 48,
                }}
              />
              <Tab
                iconPosition="start"
                label={`Acesso a Outros Sistemas ${
                  acessoOutrosSistemas.length > 0
                    ? `(${acessoOutrosSistemas.length})`
                    : ''
                }`}
                id="tab-1"
                aria-controls="tabpanel-1"
                sx={{
                  textTransform: 'none',
                  fontWeight: 'medium',
                  minHeight: 48,
                }}
              />
            </Tabs>

            {/* Conteúdo da Tab 0 - Acesso a Outros Componentes */}
            <Box
              role="tabpanel"
              hidden={activeTab !== 0}
              id="tabpanel-0"
              aria-labelledby="tab-0"
              sx={{ p: 3 }}
            >
              {activeTab === 0 && (
                <ListaAcessoOutrosComponentes
                  initialRows={acessoOutrosComponentes}
                  onAcessoOutrosComponentesChange={
                    handleAcessoOutrosComponentesChange
                  }
                />
              )}
            </Box>

            {/* Conteúdo da Tab 1 - Acesso a Outros Sistemas */}
            <Box
              role="tabpanel"
              hidden={activeTab !== 1}
              id="tabpanel-1"
              aria-labelledby="tab-1"
              sx={{ p: 3 }}
            >
              {activeTab === 1 && (
                <ListaAcessoOutrosSistemas
                  initialRows={acessoOutrosSistemas}
                  onAcessoOutrosSistemasChange={
                    handleAcessoOutrosSistemasChange
                  }
                  availableSistemas={availableSistemas}
                  isLoadingSistemas={isLoadingSistemas}
                />
              )}
            </Box>
          </Card>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancelar</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          color="primary"
          disabled={!isValid}
        >
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
};
